import { obtenerFechaHoyISO } from "../../../utils/horaLocal";
import { SEMANTIC } from "../theme/brand";

/**
 * Estado de suscripción: activa | por_vencer | vencida | no_aplica
 * - activa: dentro del periodo de vigencia, fuera del rango de recordatorio
 * - por_vencer: dentro del rango de recordatorio (días antes de vencer)
 * - vencida: fecha de fin ya pasó
 * - no_aplica: venta sin producto recurrente o sin fechas
 */
export function calcularEstadoSuscripcion(venta) {
  if (!venta.endDate) return "no_aplica";
  if (venta.anulada) return "no_aplica";

  const hoy = obtenerFechaHoyISO();
  const fin = venta.endDate;
  const diasRestantes = diasEntre(hoy, fin);

  if (diasRestantes < 0) return "vencida";

  const diasRecordatorio = venta.reminderDays ?? 7;
  if (diasRestantes <= diasRecordatorio) return "por_vencer";

  return "activa";
}

/** Días entre dos fechas ISO (YYYY-MM-DD). Positivo = futuro, negativo = pasado */
export function diasEntre(fechaDesde, fechaHasta) {
  const desde = new Date(fechaDesde + "T00:00:00");
  const hasta = new Date(fechaHasta + "T00:00:00");
  return Math.floor((hasta - desde) / 86400000);
}

/** Calcular fecha fin a partir de fecha inicio + días de vigencia */
export function calcularFechaFin(startDate, validityDays) {
  const d = new Date(startDate + "T00:00:00");
  d.setDate(d.getDate() + validityDays);
  return d.toISOString().slice(0, 10);
}

/** Días restantes de una suscripción (negativo si ya venció) */
export function diasRestantes(venta) {
  if (!venta.endDate) return null;
  return diasEntre(obtenerFechaHoyISO(), venta.endDate);
}

/** Color según estado de suscripción */
export function colorEstado(estado) {
  switch (estado) {
    case "activa": return SEMANTIC.revenue;
    case "por_vencer": return SEMANTIC.warning;
    case "vencida": return SEMANTIC.danger;
    default: return SEMANTIC.neutral;
  }
}

/** Label legible */
export function labelEstado(estado) {
  switch (estado) {
    case "activa": return "Activa";
    case "por_vencer": return "Por vencer";
    case "vencida": return "Vencida";
    default: return "";
  }
}

/** Agrupar ventas activas (no anuladas) por estado de suscripción */
export function agruparPorEstado(ventas) {
  const resultado = { activa: [], por_vencer: [], vencida: [] };
  ventas.forEach(v => {
    if (v.anulada) return;
    const estado = calcularEstadoSuscripcion(v);
    if (resultado[estado]) resultado[estado].push(v);
  });
  return resultado;
}

/** Calcular dinero en riesgo (por_vencer) y perdido (vencida) */
export function calcularDineroSuscripciones(ventas) {
  const grupos = agruparPorEstado(ventas);
  return {
    enRiesgo: grupos.por_vencer.reduce((s, v) => s + v.total, 0),
    perdido: grupos.vencida.reduce((s, v) => s + v.total, 0),
    activo: grupos.activa.reduce((s, v) => s + v.total, 0),
    countActivas: grupos.activa.length,
    countPorVencer: grupos.por_vencer.length,
    countVencidas: grupos.vencida.length,
  };
}

/** Tasa de renovación: ventas renovadas / ventas que ya vencieron + renovadas */
export function calcularTasaRenovacion(ventas) {
  const recurrentes = ventas.filter(v => !v.anulada && v.endDate);
  if (recurrentes.length === 0) return 0;
  const vencidas = recurrentes.filter(v => calcularEstadoSuscripcion(v) === "vencida");
  const renovadas = recurrentes.filter(v => v.renovadaDesde);
  const base = vencidas.length + renovadas.length;
  if (base === 0) return 100; // todas activas aún
  return Math.round((renovadas.length / base) * 100);
}
